import React from "react";
import clsx from "clsx";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";

import ProgressBar from "@/ui/molecules/ProgressBar";

import FileExtractionError from "@/ui/organisms/FileExtraction/FileExtractionError";
import type {
    DocumentType,
    FileExtractionInfo,
    FileInfoWithParent,
} from "@/ui/organisms/dealTypes";

import type { LayoutBoxCenteredProps } from "@/ui/layouts/LayoutBoxCentered";
import LayoutBoxCentered from "@/ui/layouts/LayoutBoxCentered";
import {
    ExtractionTypeSelect,
    CoaGateScreen,
    useCoaSelection,
    useCoaAutoFire,
} from "@/ui/organisms/ExtractionTypeSelect";
import Skeleton from "@/ui/atoms/Skeleton";

const skeletonLoadersCoveringWholeModal = (
    <Box
        sx={{
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            fontSize: "18px",
            color: "var(--text-color-dark)",
        }}
    >
        <Box
            sx={{
                padding: "40px",
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <Skeleton variant="text" sx={{ width: "60%" }} />
            <Skeleton
                variant="rectangular"
                sx={{ width: "100%", height: "50px" }}
            />
            <Skeleton variant="text" sx={{ width: "80%" }} />
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                }}
            >
                <Skeleton
                    variant="rectangular"
                    sx={{ width: "100%", height: "50px" }}
                />
                <Skeleton
                    variant="rectangular"
                    sx={{ width: "100%", height: "50px" }}
                />
            </Box>
            <Box
                sx={{
                    width: "80%",
                    display: "flex",
                    flexDirection: "row",
                    gap: "16px",
                }}
            >
                <Skeleton
                    variant="rectangular"
                    sx={{ width: "100%", height: "30px" }}
                />
                <Skeleton
                    variant="rectangular"
                    sx={{ width: "100%", height: "30px" }}
                />
            </Box>
        </Box>
    </Box>
);

export type ViewFileExtractionModalParams = {
    useState: (params: { projectId: number; databaseId: number }) => {
        isRentRollUploadEnabled: boolean;
        fileExtractionRequestStatus:
            | "idle"
            | "pending"
            | "resolved"
            | "rejected";
        documentType: Extract<
            DocumentType,
            "operating_statement" | "rent_roll"
        > | null;
        progressPercentage: number | null;
        progressMessage: string | null;
        error: {
            errorMessage: string;
            isExtractionNetworkError?: boolean;
        } | null;
        fileExtractionInfo: FileExtractionInfo[];
        isAiExtractionEnabled: boolean;
        // Chart-of-accounts state for the OS-upload CoA prompt (OS-3938).
        // Whether this upload is an Operating Statement one (computed once in
        // the composition so query gating and rendering can't disagree).
        isOsUpload: boolean;
        projectCoaId: number | null;
        projectDealName: string | null;
        projectDescription: string | null;
        availableCoAs: { coaId: number; name: string }[];
        availableCoAsStatus: "pending" | "rejected" | "resolved";
        // Combined load state of project details + files (needed to know the
        // current CoA and whether the project is locked).
        baselineStatus: "pending" | "rejected" | "resolved";
        // An Operating Statement extraction already exists → CoA is fixed.
        coaLocked: boolean;
        retryCoaData: () => void;
    };
    useCases: () => {
        onClose: (params: { projectId: number; databaseId: number }) => void;
        onMount: () => void;
        onUnmount: () => void;
        setDocumentType: (
            documentType: Extract<
                DocumentType,
                "operating_statement" | "rent_roll"
            >,
        ) => void;
        createExtractionFromExistingFile: (params: {
            projectId: number;
            databaseId: number;
            fileId: number;
            documentType: Extract<
                DocumentType,
                "operating_statement" | "rent_roll"
            >;
            useAiExtraction?: boolean;
        }) => Promise<void>;
        assignCoa: (params: {
            projectId: number;
            databaseId: number;
            coaId: number;
            dealName: string;
            description: string | null | undefined;
        }) => Promise<number | undefined>;
    };
};
export type ViewFileExtractionModalProps = Omit<BoxProps, "children"> & {
    LayoutBoxCenteredProps?: LayoutBoxCenteredProps;
    projectId: number;
    databaseId: number;
    fileId: number;
    handleCancelClicked: () => void;
};

export const testIds = {
    closeButton: "FileExtraction-closeButton",
    continueAsOperatingStatementButton:
        "FileExtraction-continueAsOperatingStatementButton",
    reprocessAsRentRollButton: "FileExtraction-reprocessAsRentRollButton",
    main: "ViewFileExtractionModal",
    documentTypeCard: (value: string) =>
        `ViewFileExtractionModal-documentType-card-${value}`,
    nextButton: "ViewFileExtractionModal-nextButton",
};
export const createViewFileExtractionModal = ({
    useState,
    useCases,
}: ViewFileExtractionModalParams) => {
    const ViewFileExtractionModal = (props: ViewFileExtractionModalProps) => {
        const {
            sx,
            className,
            LayoutBoxCenteredProps,
            projectId,
            databaseId,
            fileId,
            handleCancelClicked,
            ...otherProps
        } = props;

        const {
            onClose,
            onMount,
            onUnmount,
            setDocumentType,
            createExtractionFromExistingFile,
            assignCoa,
        } = useCases();

        const {
            fileExtractionInfo,
            fileExtractionRequestStatus,
            error,
            progressPercentage,
            progressMessage,
            documentType,
            isRentRollUploadEnabled,
            isAiExtractionEnabled,
            isOsUpload,
            projectCoaId,
            projectDealName,
            projectDescription,
            availableCoAs,
            availableCoAsStatus,
            baselineStatus,
            coaLocked,
            retryCoaData,
        } = useState({ projectId, databaseId });

        const files = React.useMemo(() => {
            const filterOutExtractions = (
                info: FileExtractionInfo,
            ): info is FileInfoWithParent =>
                "isParent" in info && info.isParent;
            return fileExtractionInfo.filter(filterOutExtractions);
        }, [fileExtractionInfo]);
        const matchedFile = React.useMemo(
            () => files.find(file => file.fileId === fileId),
            [files, fileId],
        );
        const selectedFileName = matchedFile?.originalFilename;

        const idle = fileExtractionRequestStatus === "idle";
        const uploading = fileExtractionRequestStatus === "pending";
        const success = fileExtractionRequestStatus === "resolved";

        // --- Chart-of-accounts prompt (OS-3938 / OS-3983) ---------------------
        // An OS upload to a Base-CoA project prompts for a CoA — but only when
        // a real choice exists (unlocked, more than one permitted CoA). A sole
        // permitted CoA is assigned silently, and rent-roll uploads, assigned
        // projects, and locked projects see no prompt at all.
        const { coaState, coaBlocksContinue, assignCoaThenRun } =
            useCoaSelection({
                isOperatingStatement: isOsUpload,
                baselineStatus,
                coaLocked,
                projectCoaId,
                projectDealName,
                projectDescription,
                availableCoAs,
                availableCoAsStatus,
                retryCoaData,
                projectId,
                databaseId,
                assignCoa,
            });

        const documentTypeRef = React.useRef(documentType);
        React.useEffect(() => {
            documentTypeRef.current = documentType;
        }, [documentType]);
        const handleDocumentTypeSelected = React.useCallback(
            (
                nextDocumentType: Extract<
                    DocumentType,
                    "operating_statement" | "rent_roll"
                >,
            ) => {
                documentTypeRef.current = nextDocumentType;
                setDocumentType(nextDocumentType);
            },
            [setDocumentType],
        );

        // Assign the chosen CoA before creating the extraction — never upload an
        // OS to an unassigned project. The hook guards re-entrancy and skips the
        // extraction when the assign fails.
        const assignCoaThenExtract = (
            documentTypeToExtract: Extract<
                DocumentType,
                "operating_statement" | "rent_roll"
            >,
        ) => {
            void assignCoaThenRun(effectiveProjectId => {
                void createExtractionFromExistingFile({
                    projectId: effectiveProjectId,
                    databaseId,
                    fileId,
                    documentType: documentTypeToExtract,
                });
            });
        };

        // Rent-Roll-disabled flow: no document-type step. The OS extraction
        // fires by itself once the CoA checks settle and nothing needs the
        // user; the gate screen appears only for an error, a required choice,
        // or a failed silent assign.
        const onAutoFire = React.useCallback(
            (effectiveProjectId: number) => {
                void createExtractionFromExistingFile({
                    projectId: effectiveProjectId,
                    databaseId,
                    fileId,
                    documentType: "operating_statement",
                });
            },
            [createExtractionFromExistingFile, databaseId, fileId],
        );
        const { coaGateNeeded } = useCoaAutoFire({
            enabled: !isRentRollUploadEnabled && idle && error === null,
            fileId,
            coaState,
            coaBlocksContinue,
            assignCoaThenRun,
            onFire: onAutoFire,
        });
        React.useEffect(() => {
            // Clear any state left behind by a previous upload surface (e.g. a
            // stale documentType from ViewFileUpload) before this flow starts.
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const handleNext = () => {
            const selectedDocumentType = documentTypeRef.current;
            if (
                !selectedDocumentType ||
                selectedFileName == null ||
                selectedFileName.trim() === "" ||
                // Never proceed while the CoA gate is loading, errored, or unpicked.
                coaBlocksContinue
            ) {
                return;
            }
            if (
                selectedDocumentType === "operating_statement" &&
                coaState.kind === "ready"
            ) {
                assignCoaThenExtract(selectedDocumentType);
                return;
            }
            void createExtractionFromExistingFile({
                projectId,
                databaseId,
                fileId,
                documentType: selectedDocumentType,
            });
        };

        const extractingFile = () => {
            return (
                <Box
                    sx={{
                        padding: "80px",
                    }}
                >
                    <Box
                        id="processing-wrapper"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            className="status-header"
                            sx={{
                                fontSize: "2rem",
                                fontWeight: 600,
                                color: "var(--text-color-dark)",
                            }}
                        >
                            The file is being processed...
                        </Box>
                        <Box
                            component={"p"}
                            className="process-subheader"
                            sx={{
                                color: "var(--proda-black-10)",
                                fontWeight: "400 !important",
                                width: "55%",
                                marginTop: "16px",
                                marginBottom: "16px",
                            }}
                        >
                            Do not close the window while the file is being
                            processed.
                        </Box>
                    </Box>

                    <ProgressBar
                        label={selectedFileName ?? ""}
                        subLabel={progressMessage}
                        progressPercentage={progressPercentage ?? 0}
                    />
                </Box>
            );
        };

        // Standalone CoA screen for the Rent-Roll-disabled flow (which has no
        // document-type step). Shown only when useCoaAutoFire couldn't proceed
        // on its own; its Continue retries the same assign-then-extract.
        const coaGateScreen = () => (
            <CoaGateScreen
                coaState={coaState}
                onContinue={() => assignCoaThenExtract("operating_statement")}
                onCancel={handleCancelClicked}
            />
        );

        return (
            <Box
                data-testid={testIds.main}
                className={clsx([testIds.main, className])}
                sx={{
                    ...sx,
                }}
                {...otherProps}
            >
                <LayoutBoxCentered
                    sx={{
                        width: "45%",
                        minWidth: "500px",
                        "@media (max-width: 600px)": {
                            width: "85%",
                        },
                        ...LayoutBoxCenteredProps?.sx,
                    }}
                    {...LayoutBoxCenteredProps}
                >
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            boxSizing: "border-box",
                            fontSize: "18px",
                            color: "var(--text-color-dark)",
                        }}
                    >
                        {error !== null ? (
                            <FileExtractionError
                                error={error}
                                selectedFileName={selectedFileName}
                                onClose={() =>
                                    onClose({ projectId, databaseId })
                                }
                                onLlmReExtract={
                                    isAiExtractionEnabled &&
                                    selectedFileName
                                        ?.toLowerCase()
                                        .endsWith(".pdf") === true
                                        ? () => {
                                              void createExtractionFromExistingFile(
                                                  {
                                                      projectId,
                                                      databaseId,
                                                      fileId,
                                                      documentType:
                                                          "operating_statement",
                                                      useAiExtraction: true,
                                                  },
                                              );
                                          }
                                        : undefined
                                }
                            />
                        ) : null}
                        {idle &&
                            isRentRollUploadEnabled &&
                            (selectedFileName !== undefined ? (
                                <ExtractionTypeSelect
                                    selectedFileName={selectedFileName}
                                    documentType={documentType}
                                    setDocumentType={handleDocumentTypeSelected}
                                    handleNext={handleNext}
                                    handleCancelClicked={handleCancelClicked}
                                    coaState={coaState}
                                />
                            ) : (
                                skeletonLoadersCoveringWholeModal
                            ))}
                        {idle &&
                            !isRentRollUploadEnabled &&
                            (coaGateNeeded
                                ? coaGateScreen()
                                : skeletonLoadersCoveringWholeModal)}
                        {(uploading || success) && extractingFile()}
                    </Box>
                </LayoutBoxCentered>
            </Box>
        );
    };
    return ViewFileExtractionModal;
};
